/** @file Dopesheet key edits as command batches: move, duplicate, delete, copy and paste. */
import { animationKeys, type KeyPiece } from '@pose2d/api/queries/animationKeys';
import { IK_CHANNELS } from '@pose2d/api/queries/ikChannels.data';
import { nameIn } from '@pose2d/api/queries/nameIn';
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import type { KeyRef } from '../../../stores/types';
import { dopeKeys } from './dopeKeys';

/** Copied keys in the form the key commands take (names, absolute values, curves) and the first copied frame. */
export interface KeyClip {
  origin: number;
  pieces: KeyPiece[];
}

/** The first path segment animationKeys gives a key of each KeyRef channel (draw order aside). */
type Head = 'bones' | 'slots' | 'ik' | 'deform' | 'events';
const HEAD: Readonly<Record<string, Head>> = {
  ...Object.fromEntries(['rotation', 'x', 'y', 'scaleX', 'scaleY', 'shearX', 'shearY', 'inherit'].map((c) => [c, 'bones'])),
  ...Object.fromEntries(['attachment', 'rgb', 'alpha', 'dark'].map((c) => [c, 'slots'])),
  ...Object.fromEntries(Object.values(IK_CHANNELS).map((c) => [c, 'ik'])),
  deform: 'deform',
  event: 'events',
};
const TABLE: Readonly<Record<Head, (doc: Doc) => Record<Id, { name: string }>>> = {
  bones: (d) => d.bones, slots: (d) => d.slots, ik: (d) => d.constraints, deform: (d) => d.attachments, events: (d) => d.events,
};

/** One timeline's selector with its ascending unique frames. */
function byTimeline(refs: readonly KeyRef[]): { animation: Id; selector: { channel: string; target?: string }; frames: number[] }[] {
  const out = new Map<string, { animation: Id; selector: { channel: string; target?: string }; frames: number[] }>();
  for (const ref of refs) {
    const key = `${ref.animation}|${ref.target}|${ref.channel}`;
    const entry = out.get(key) ?? { animation: ref.animation, selector: dopeKeys.selector(ref), frames: [] };
    entry.frames.push(ref.frame);
    out.set(key, entry);
  }
  return [...out.values()].map((e) => ({ ...e, frames: [...new Set(e.frames)].sort((a, b) => a - b) }));
}

/** The same string for a KeyRef and for the KeyPiece animationKeys returns for that key. */
function refPieceId(doc: Doc, ref: KeyRef): string {
  const frame = String(ref.frame);
  if (ref.channel === 'drawOrder') return `drawOrder|${frame}`;
  const head = HEAD[ref.channel] ?? 'bones';
  const lane = head === 'deform' || head === 'events' ? [] : [ref.channel];
  return [head, nameIn(TABLE[head](doc))(ref.target), ...lane, frame].join('|');
}

/** Path, the event name for an event key, and the frame. */
function pieceId(piece: KeyPiece): string {
  const lane = piece.path[0] === 'events' ? [String(piece.value['event'])] : [];
  return [...piece.path, ...lane, String(piece.value['frame'])].join('|');
}

/** The key command that writes `piece` at `frame` of `animation`. */
function callOf(animation: Id, piece: KeyPiece, frame: number): CommandCall {
  const [group, target, channel] = piece.path;
  const value = { ...piece.value, frame };
  if (group === 'deform') return { name: 'animation.setDeformKey', input: { animation, attachment: target, ...value } };
  if (group === 'drawOrder') return { name: 'animation.setDrawOrderKey', input: { animation, ...value } };
  if (group === 'events') return { name: 'animation.setEventKey', input: { animation, ...value } };
  return { name: 'animation.setKey', input: { animation, target, channel, ...value } };
}

const minFrame = (refs: readonly KeyRef[]): number => refs.reduce((m, r) => Math.min(m, r.frame), Number.POSITIVE_INFINITY);

/** Each operation returns the batch to dispatch as one command list (one undo step). */
export const keyCommands = {
  /** `delta` raised so that no key would move before frame 0 (moveKeys refuses that). */
  clampDelta(refs: readonly KeyRef[], delta: number): number {
    return refs.length === 0 ? 0 : Math.max(delta, -minFrame(refs));
  },
  /** One animation.moveKeys per timeline; a moved key replaces an unselected key it lands on. */
  move(refs: readonly KeyRef[], delta: number): CommandCall[] {
    if (delta === 0) return [];
    return byTimeline(refs).map((t) => ({ name: 'animation.moveKeys', input: { animation: t.animation, ...t.selector, frames: t.frames, delta } }));
  },
  /** One animation.deleteKeys per timeline. */
  remove(refs: readonly KeyRef[]): CommandCall[] {
    return byTimeline(refs).map((t) => ({ name: 'animation.deleteKeys', input: { animation: t.animation, ...t.selector, frames: t.frames } }));
  },
  /** The selected keys (all of one animation) as a clip; null when none of them exists in `doc`. */
  copy(doc: Doc, refs: readonly KeyRef[]): KeyClip | null {
    const anim = refs[0] === undefined ? undefined : doc.animations[refs[0].animation];
    if (anim === undefined) return null;
    const origin = minFrame(refs);
    const last = refs.reduce((m, r) => Math.max(m, r.frame), origin);
    const wanted = new Set(refs.map((r) => refPieceId(doc, r)));
    const pieces = animationKeys(doc, anim, { keys: true, frames: [origin, last] }).filter((p) => wanted.has(pieceId(p)));
    return pieces.length === 0 ? null : { origin, pieces };
  },
  /** The clip written into `animation` with its first frame at `frame` (other frames keep their offsets). */
  paste(clip: KeyClip, animation: Id, frame: number): CommandCall[] {
    return clip.pieces.map((p) => callOf(animation, p, frame + Number(p.value['frame']) - clip.origin));
  },
  /** Alt-drag: copies of the keys `delta` frames away (originals stay). */
  duplicate(doc: Doc, refs: readonly KeyRef[], delta: number): CommandCall[] {
    const clip = keyCommands.copy(doc, refs);
    const first = refs[0];
    return clip === null || first === undefined || delta === 0 ? [] : keyCommands.paste(clip, first.animation, clip.origin + delta);
  },
};
